import type { PurchaseOrderReceiptResult, ReceiptTrackingResolutions, ReceiptInventoryRecord } from '@/lib/shipments/po-receipt';
import { supabase } from '@/lib/supabase/client';
import type { ComponentInventory, ComponentInventoryInsert, ComponentInventoryUpdate } from '@/types/component-inventory';

// Helper to get current user ID
async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

function inventoryInsertData(data: ComponentInventoryInsert, userId: string | null) {
  return {
    component_id: data.component_id,
    serial_number: data.serial_number ?? null,
    purchase_date: data.purchase_date ?? null,
    purchase_cost: data.purchase_cost ?? null,
    vendor: data.vendor ?? null,
    purchase_order_id: data.purchase_order_id ?? null,
    reserve_location: data.reserve_location ?? null,
    quantity: data.quantity ?? 1,
    received_date: data.received_date ?? null,
    assignment_status: data.assignment_status ?? 'planned',
    assigned_order_id: data.assigned_order_id ?? null,
    assigned_location_id: data.assigned_location_id ?? null,
    ship_date: data.ship_date ?? null,
    installed_date: data.installed_date ?? null,
    warranty_start_date: data.warranty_start_date ?? null,
    warranty_period_days: data.warranty_period_days ?? null,
    warranty_expiration_date: data.warranty_expiration_date ?? null,
    is_refurbished: data.is_refurbished ?? false,
    notes: data.notes ?? null,
    created_by: userId,
    updated_by: userId,
      };
}

async function readInventoryPages(column: 'purchase_order_id' | 'assigned_location_id', value: string): Promise<ComponentInventory[]> {
  const records: ComponentInventory[] = [];
  for (let offset = 0; ; offset += 1000) {
    const { data, error } = await supabase.from('component_inventory').select('*').eq(column, value)
      .order('created_at', { ascending: true }).order('id', { ascending: true }).range(offset, offset + 999);
    if (error) throw error;
    records.push(...(data || []) as ComponentInventory[]);
    if (!data || data.length < 1000) return records;
  }
}

export const componentInventoryService = {
  async receivePurchaseOrder(purchaseOrderId: string, items: ReceiptInventoryRecord[], expectedInventory: ComponentInventory[], resolutions: ReceiptTrackingResolutions = {}): Promise<PurchaseOrderReceiptResult> {
    const userId = await getCurrentUserId();
    const { data, error } = await supabase.rpc('receive_purchase_order_inventory', {
      p_purchase_order_id: purchaseOrderId,
      p_records: items.map(item => ({ ...inventoryInsertData(item, userId), id: item.id })),
      p_expected_inventory: expectedInventory.map(item => ({id: item.id, component_id: item.component_id, quantity: item.quantity, reserve_location: item.reserve_location, ship_date: item.ship_date, assigned_location_id: item.assigned_location_id})),
      p_resolutions: resolutions,
    });
    if (error) throw new Error(`Failed to receive purchase order: ${error.message}`);
    if (data?.status !== 'saved' && data?.status !== 'conflict') throw new Error('Invalid purchase order receipt response');
    return data as PurchaseOrderReceiptResult;
  },

  async getAll(strict = false): Promise<ComponentInventory[]> {
    try {
      const { data, error } = await supabase.from('component_inventory').select('*').order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching component inventory:', error);
        if (strict) throw error;
        return [];
      }

      return (data as ComponentInventory[]) || [];
    } catch (error) {
      if (strict) throw error;
      console.error('Error fetching component inventory:', error);
      return [];
    }
  },

  async getByPurchaseOrderId(purchaseOrderId: string): Promise<ComponentInventory[]> {
    return readInventoryPages('purchase_order_id', purchaseOrderId);
  },

  async getBySerialNumbers(serials: string[]): Promise<ComponentInventory[]> {
    const records: ComponentInventory[] = [];
    for (let index = 0; index < serials.length; index += 100) {
      const { data, error } = await supabase.from('component_inventory').select('*').in('serial_number', serials.slice(index, index + 100));
      if (error) throw error;
      records.push(...(data || []) as ComponentInventory[]);
    }
    return records;
  },

  async getById(id: string): Promise<ComponentInventory | null> {
    try {
      const { data, error } = await supabase.from('component_inventory').select('*').eq('id', id).single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching component inventory:', error);
        return null;
      }

      return (data as ComponentInventory) || null;
    } catch (error) {
      console.error('Error fetching component inventory:', error);
      return null;
    }
  },

  async getByComponentId(componentId: string): Promise<ComponentInventory[]> {
    try {
      const { data, error } = await supabase
        .from('component_inventory')
        .select('*')
        .eq('component_id', componentId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching component inventory by component ID:', error);
        return [];
      }

      return (data as ComponentInventory[]) || [];
    } catch (error) {
      console.error('Error fetching component inventory by component ID:', error);
      return [];
    }
  },

  async getBySerialNumber(serialNumber: string): Promise<ComponentInventory | null> {
    try {
      const { data, error } = await supabase
        .from('component_inventory')
        .select('*')
        .eq('serial_number', serialNumber)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching component inventory by serial number:', error);
        return null;
      }

      return (data as ComponentInventory) || null;
    } catch (error) {
      console.error('Error fetching component inventory by serial number:', error);
      return null;
    }
  },

  async getByReserveLocation(location: string): Promise<ComponentInventory[]> {
    try {
      const { data, error } = await supabase
        .from('component_inventory')
        .select('*')
        .eq('reserve_location', location)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching component inventory by reserve location:', error);
        return [];
      }

      return (data as ComponentInventory[]) || [];
    } catch (error) {
      console.error('Error fetching component inventory by reserve location:', error);
      return [];
    }
  },

  async getByWarehouse(warehouse: string): Promise<ComponentInventory[]> {
    try {
      const { data, error } = await supabase
        .from('component_inventory')
        .select('*')
        .eq('reserve_location', warehouse)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching component inventory by warehouse:', error);
        return [];
      }

      return (data as ComponentInventory[]) || [];
    } catch (error) {
      console.error('Error fetching component inventory by warehouse:', error);
      return [];
    }
  },

  async getAvailable(): Promise<ComponentInventory[]> {
    try {
      const { data, error } = await supabase
        .from('component_inventory')
        .select('*')
        .in('assignment_status', ['received', 'reserved'])
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching available component inventory:', error);
        return [];
      }

      return (data as ComponentInventory[]) || [];
    } catch (error) {
      console.error('Error fetching available component inventory:', error);
      return [];
    }
  },

  async getByLocationId(locationId: string, strict = false): Promise<ComponentInventory[]> {
    if (strict) return readInventoryPages('assigned_location_id', locationId);
    try {
      const { data, error } = await supabase
        .from('component_inventory')
        .select('*')
        .eq('assigned_location_id', locationId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching component inventory by location ID:', error);
        if (strict) throw error;
        return [];
      }

      return (data as ComponentInventory[]) || [];
    } catch (error) {
      if (strict) throw error;
      console.error('Error fetching component inventory by location ID:', error);
      return [];
    }
  },

  async getByOrderId(orderId: string): Promise<ComponentInventory[]> {
    try {
      const { data, error } = await supabase
        .from('component_inventory')
        .select('*')
        .eq('assigned_order_id', orderId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching component inventory by order ID:', error);
        return [];
      }

      return (data as ComponentInventory[]) || [];
    } catch (error) {
      console.error('Error fetching component inventory by order ID:', error);
      return [];
    }
  },

  async create(data: ComponentInventoryInsert): Promise<ComponentInventory> {
    try {
      const userId = await getCurrentUserId();

      const insertData = inventoryInsertData(data, userId);

      const { data: newInventory, error } = await supabase.from('component_inventory').insert(insertData).select().single();

      if (error) {
        console.error('Error creating component inventory:', error);
        throw new Error(`Failed to create component inventory: ${error.message}`);
      }

      return newInventory as ComponentInventory;
    } catch (error) {
      console.error('Error creating component inventory:', error);
      throw error;
    }
  },

  async createMany(items: ComponentInventoryInsert[]): Promise<ComponentInventory[]> {
    if (!items.length) return [];
    const userId = await getCurrentUserId();
    const { data, error } = await supabase.from('component_inventory')
      .insert(items.map(item => inventoryInsertData(item, userId))).select();
    if (error) throw new Error(`Failed to create component inventory: ${error.message}`);
    return (data || []) as ComponentInventory[];
  },

  async update(id: string, data: ComponentInventoryUpdate): Promise<ComponentInventory> {
    try {
      const userId = await getCurrentUserId();

      const updateData: Record<string, unknown> = {
        updated_by: userId,
      };

      // Only include fields that are provided
      if (data.component_id !== undefined) updateData.component_id = data.component_id;
      if (data.serial_number !== undefined) updateData.serial_number = data.serial_number;
      if (data.purchase_date !== undefined) updateData.purchase_date = data.purchase_date;
      if (data.purchase_cost !== undefined) updateData.purchase_cost = data.purchase_cost;
      if (data.vendor !== undefined) updateData.vendor = data.vendor;
      if (data.purchase_order_id !== undefined) updateData.purchase_order_id = data.purchase_order_id;
      if (data.reserve_location !== undefined) updateData.reserve_location = data.reserve_location;
      if (data.quantity !== undefined) updateData.quantity = data.quantity;
      if (data.received_date !== undefined) updateData.received_date = data.received_date;
      if (data.assignment_status !== undefined) updateData.assignment_status = data.assignment_status;
      if (data.assigned_order_id !== undefined) updateData.assigned_order_id = data.assigned_order_id;
      if (data.assigned_location_id !== undefined) updateData.assigned_location_id = data.assigned_location_id;
      if (data.ship_date !== undefined) updateData.ship_date = data.ship_date;
      if (data.installed_date !== undefined) updateData.installed_date = data.installed_date;
      if (data.warranty_start_date !== undefined) updateData.warranty_start_date = data.warranty_start_date;
      if (data.warranty_period_days !== undefined) updateData.warranty_period_days = data.warranty_period_days;
      if (data.warranty_expiration_date !== undefined) updateData.warranty_expiration_date = data.warranty_expiration_date;
      if (data.is_refurbished !== undefined) updateData.is_refurbished = data.is_refurbished;
      if (data.notes !== undefined) updateData.notes = data.notes;

      const { data: updatedInventory, error } = await supabase
        .from('component_inventory')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating component inventory:', error);
        throw new Error(`Failed to update component inventory: ${error.message}`);
      }

      if (!updatedInventory) {
        throw new Error('Component inventory not found');
      }

      return updatedInventory as ComponentInventory;
    } catch (error) {
      console.error('Error updating component inventory:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase.from('component_inventory').delete().eq('id', id);

      if (error) {
        console.error('Error deleting component inventory:', error);
        throw new Error(`Failed to delete component inventory: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting component inventory:', error);
      throw error;
    }
  },
};

